import React, { type RefObject, useEffect } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import type { PreloadedEntryPoint } from 'react-relay';

import noop from '@atlaskit/ds-lib/noop';
import type { ModalDialogProps } from '@atlaskit/modal-dialog/types';
import { getFirstFocusable } from '@atlaskit/top-layer/get-first-focusable';

type FocusInsideOnLoadProps = {
	/**
	 * The preloaded entry point reference. Reading `getComponent()` will throw
	 * the loading promise (per react-relay's `loadEntryPoint`) until the entry
	 * point's module has finished loading. When wrapped in a `<Suspense>`
	 * boundary this means the component will not mount until the entry point
	 * is ready to render.
	 */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	entryPointReference: PreloadedEntryPoint<any>;

	autoFocusRef?: ModalDialogProps['autoFocus'];

	modalRef: RefObject<HTMLElement>;
};

const FocusInsideOnLoadInner = ({
	autoFocusRef,
	entryPointReference,
	modalRef,
}: FocusInsideOnLoadProps): null => {
	// Suspends until the entry point's module has finished loading. The result
	// is intentionally unused — this call exists purely to gate the mount of
	// this component on the load completing.
	entryPointReference.getComponent();

	useEffect(() => {
		// Fence for if container element doesn't exist, but this is effectively not
		// possible.
		if (!modalRef.current) {
			return;
		}
		// If ref provided, use that first. Else find first focusable element or
		// container if not exists
		if (autoFocusRef?.current) {
			autoFocusRef.current.focus();
			return;
		}

		const firstFocusable = getFirstFocusable({ container: modalRef.current });
		if (firstFocusable) {
			firstFocusable.focus();
			return;
		}

		modalRef.current.focus();
	}, [autoFocusRef, modalRef]);

	return null;
};

const renderNothing = () => null;

/**
 * Moves focus into the modal once the entry point has finished
 * loading, so makers don't have to manage focus manually after
 * the loading fallback is replaced by the final experience.
 *
 * It suspends on the loading of the provided `entryPointReference` and, once
 * the experience has loaded, focuses on expected element.
 *
 * The component manages its own Suspense + error boundary so it can be
 * dropped in alongside the entry point container without interfering with
 * the loading fallback or the container's existing error handling. Errors
 * from a failed entry-point load are already reported and handled by
 * `InternalEntryPointContainer`'s own ErrorBoundary (which closes the
 * modal), so the boundary here is intentionally silent — it just prevents
 * this focus-management side-effect from surfacing a duplicate error or an
 * unhandled rejection.
 */
export const FocusInsideOnLoad = ({
	entryPointReference,
	autoFocusRef,
	modalRef,
}: FocusInsideOnLoadProps): React.JSX.Element => (
	<ErrorBoundary FallbackComponent={renderNothing} onError={noop}>
		<React.Suspense fallback={null}>
			<FocusInsideOnLoadInner
				entryPointReference={entryPointReference}
				autoFocusRef={autoFocusRef}
				modalRef={modalRef}
			/>
		</React.Suspense>
	</ErrorBoundary>
);
